import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

export default function SeatBookingModel({ open, onClose, seatData, selectedSeatsData, seatsCount }) {
    const [selectedSeats, setSelectedSeats] = useState([]);
    const modalRef = useRef(null);

    // Memoized lookup for efficient access
    const seatLookup = useMemo(() => {
        const map = {};
        seatData.forEach(seat => {
            map[seat.name] = seat;
        });
        return map;
    }, [seatData]);

    // Memoized seat map for layout rendering
    const seatMap = useMemo(() => {
        const map = {};
        let maxRow = 0;
        let maxCol = 0;

        seatData.forEach(seat => {
            const row = seat.row;
            const col = seat.column;

            if (!map[row]) map[row] = {};
            map[row][col] = seat;

            maxRow = Math.max(maxRow, row);
            maxCol = Math.max(maxCol, col);
        });

        return {
            map,
            rows: maxRow,
            columns: maxCol
        };
    }, [seatData]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [open, onClose]);

    const getRowName = (index) => String.fromCharCode(64 + index); // 1 => A

    // const toggleSeatSelection = (seatId) => {
    //     if(seatsCount <= selectedSeats.length)
    //     setSelectedSeats(prev =>
    //         prev.includes(seatId)
    //             ? prev.filter(id => id !== seatId)
    //             : [...prev, seatId]
    //     );
    // };
    const toggleSeatSelection = (seatId) => {
        setSelectedSeats(prev => {
            if (prev.includes(seatId)) {
                // Remove if already selected
                return prev.filter(id => id !== seatId);
            }
            // Add new seat, remove oldest if over limit
            if (prev.length >= seatsCount) {
                return [...prev.slice(1), seatId];
            }
            return [...prev, seatId];
        });
    };

    const handleSave = () => {
        const selectedSeatDetails = selectedSeats.map(name => seatLookup[name]);
        selectedSeatsData(selectedSeatDetails);
        onClose();
    };

    if (!open) return null;

    const renderSeats = () => {
        const layout = [];

        const header = [<div key="top-left" className="w-8 h-8" />];
        for (let col = 1; col <= seatMap.columns; col++) {
            header.push(
                <div key={`col-${col}`} className="w-8 h-8 flex items-center justify-center text-sm font-medium text-gray-600">
                    {col}
                </div>
            );
        }
        header.push(<div key="top-right" className="w-8 h-8" />);
        layout.push(<div key="header" className="flex mb-2">{header}</div>);

        for (let row = 1; row <= seatMap.rows; row++) {
            const rowCells = [];
            const rowLabel = getRowName(row);

            rowCells.push(
                <div key={`row-label-left-${row}`} className="w-8 h-8 flex items-center justify-center font-medium text-gray-600">
                    {rowLabel}
                </div>
            );

            for (let col = 1; col <= seatMap.columns; col++) {
                const seat = seatMap.map[row]?.[col];
                if (seat) {
                    const isSelected = selectedSeats.includes(seat.name);
                    rowCells.push(
                        <div
                            key={seat.name}
                            onClick={() => toggleSeatSelection(seat.name)}
                            className={`w-8 h-8 m-0.5 flex items-center justify-center rounded cursor-pointer text-xs transition-colors
                            ${isSelected ? 'bg-blue-500 text-white' : 'bg-gray-300 hover:bg-gray-400 text-gray-800'}`}
                        >
                            {seat.name}
                        </div>
                    );
                } else {
                    rowCells.push(
                        <div key={`empty-${row}-${col}`} className="w-8 h-8 m-0.5" />
                    );
                }
            }

            rowCells.push(
                <div key={`row-label-right-${row}`} className="w-8 h-8 flex items-center justify-center font-medium text-gray-600">
                    {rowLabel}
                </div>
            );

            layout.push(
                <div key={`row-${row}`} className="flex mb-1">
                    {rowCells}
                </div>
            );
        }

        return layout;
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div
                ref={modalRef}
                className="bg-white rounded-lg shadow-xl w-11/12 max-w-4xl max-h-[90vh] overflow-y-auto"
            >
                <div className="p-4 border-b flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-800">Seat Layout</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="p-6">
                    <div className="overflow-x-auto pb-4 max-h-[50vh]">
                        <div className="min-w-max flex flex-col items-center">
                            {seatData.length === 0 ? (
                                <p className="text-gray-500 text-center mt-4">No seats to select</p>
                            ) : (
                                renderSeats()
                            )}
                        </div>
                    </div>

                    {selectedSeats.length > 0 && (
                        <div className="mt-6 p-4 bg-gray-100 rounded-lg">
                            <h2 className="font-medium mb-2">Selected Seats: {selectedSeats.length}</h2>
                            <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                                {selectedSeats.map(seat => (
                                    <span key={seat} className="px-2 py-1 bg-blue-600 text-white rounded text-sm">
                                        {seat}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="border-t p-4 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={selectedSeats.length !== seatsCount}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Continue
                    </button>
                </div>
            </div>
        </div>
    );
}

SeatBookingModel.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    seatData: PropTypes.array.isRequired,
    selectedSeatsData: PropTypes.func.isRequired,
    seatsCount: PropTypes.number.isRequired,
};
